import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Gestures } from 'react-gesture-handler';

import { ICell, IHuntWord, ISolution, LayoutProps } from '../huntWordInterface';
import { Container, Letter, Square } from './styles';

interface block {
  layout?: LayoutProps;
  index: number;
  letter: string;
  x: string;
  y: string;
  isCorrect: boolean;
  ref: HTMLDivElement;
}

interface GameProps {
  i: number; //quantidade de colunas
  j: number; //quantidade de linhas
  solutions: ISolution[];
  data: IHuntWord;
  isFinished: boolean;
  showWord: boolean;
  showLetter: boolean;
  width: number;
  resetGame: boolean;
  acceptResponse: (el: ISolution) => void;
  toggleShowWordHint: () => void;
  toggleShowLetterHint: () => void;
  toggleResetGame: () => void;
}

//Variáveis de auxilo.
//Fora do componente para não sofrerem alteração em mudança de estado.
//Não são estados para não causarem renderização.
let preventDefault = -1;
let track: number[] = [];
const blockMargin = 0;

const Game: React.FC<GameProps> = ({
  isFinished,
  toggleShowWordHint,
  toggleShowLetterHint,
  toggleResetGame,
  showWord,
  showLetter,
  resetGame,
  width,
  ...props
}) => {
  const blocks = useRef<block[]>([]);

  //Setup inicial
  const preventDirectionV = props.i;
  const preventDirectionD1 = props.i + 1;
  const preventDirectionD2 = props.i - 1;
  const numBlocks = props.i * props.j; //Número total de blocos
  const offset = 0; //Offset para detecção do toque
  const hoverColor = '#bc6ce4'; //Cor quando o dedo deslizar por cima do bloco
  const acceptedColor = 'rgb(84, 173, 72)'; //Cor de palavra encontrada

  useEffect(() => {
    window.onresize = function () {
      for (let block of blocks.current) {
        let { ref } = block;
        block.layout = {
          height: ref?.offsetHeight,
          width: ref?.offsetWidth,
          x: ref?.offsetLeft,
          y: ref?.offsetTop,
        };
      }
    };
  }, []);

  //Elemento bloco
  const blockElement = (index: number, celula: ICell) => (
    <Square
      selecting={false}
      amountColumns={props.i}
      amountLines={props.j}
      ref={(ref: HTMLDivElement) => {
        blocks.current.push({
          index,
          ref,
          letter: celula.attributes.solution,
          x: celula.attributes.x,
          y: celula.attributes.y,
          isCorrect: false,
          layout: {
            height: ref?.offsetHeight,
            width: ref?.offsetWidth,
            x: ref?.offsetLeft,
            y: ref?.offsetTop,
          },
        });
      }}
      key={index}
    >
      <Letter>{celula.attributes.solution}</Letter>
    </Square>
  );

  //Criação dos blocos
  const createBlocks = (n: number) => {
    let elements: JSX.Element[] = [];

    for (let i = 0; i < n; i++) {
      elements.push(blockElement(i, props.data.grid.cell[i]));
    }

    return elements;
  };

  //Matrix como estado não sofre renderização duas vezes, diminuindo o processamento da matriz
  const [matrix] = useState(createBlocks(numBlocks));

  //Onde a mágica acontece
  const tracker = (x: number, y: number) => {
    for (let block of blocks.current) {
      if (!block.layout) break;

      //Função de aproximação
      if (
        x >= block.layout.x + offset &&
        x <= block.layout.x + block.layout.width - offset &&
        y >= block.layout.y + offset &&
        y <= block.layout.y + block.layout.height - offset
      ) {
        //Evita que o mesmo bloco seja processado duas vezes
        if (preventDefault === block.index) break;

        preventDefault = block.index;

        //Caso seja a primeira seleção
        if (track.length === 0) {
          block.ref.style.background = hoverColor;
          track.push(block.index);
          break;
        }

        let initialBlock = blocks.current[track[0]];

        if (!initialBlock.layout) break;

        //Distância(em blocos) do atual bloco ao inicial
        let X = Math.round(
          (block.layout.x - initialBlock.layout.x) /
            (block.layout.width + blockMargin * 2)
        );
        let Y = Math.round(
          (block.layout.y - initialBlock.layout.y) /
            (block.layout.height + blockMargin * 2)
        );

        //Testes de direção
        if (initialBlock.index + 1 * X === block.index)
          newDirection(initialBlock.index, block.index, 1);
        if (initialBlock.index + preventDirectionV * Y === block.index)
          newDirection(initialBlock.index, block.index, preventDirectionV);
        if (initialBlock.index + preventDirectionD1 * Y === block.index)
          newDirection(initialBlock.index, block.index, preventDirectionD1);
        if (initialBlock.index + preventDirectionD2 * Y === block.index)
          newDirection(initialBlock.index, block.index, preventDirectionD2);
      }
    }
  };

  const newDirection = (i: number, i2: number, taxa: number) => {
    //Reseta a cor dos blocos selecionados
    updateTable();

    track = [];

    //Aplica cor aos novos blocos
    if (i < i2)
      for (; i <= i2; i += taxa) {
        blocks.current[i].ref.style.background = hoverColor;
        track.push(blocks.current[i].index);
      }
    else
      for (; i >= i2; i -= taxa) {
        blocks.current[i].ref.style.background = hoverColor;
        track.push(blocks.current[i].index);
      }
  };

  const updateTable = () => {
    for (let block of blocks.current) {
      block.isCorrect
        ? (block.ref.style.background = acceptedColor)
        : (block.ref.style.background = 'transparent');
    }
  };

  //Função de validação
  const validar = () => {
    for (let solution of props.solutions) {
      if (!track[0]) break;

      if (
        (solution.x[0] === blocks.current[track[0]].x &&
          solution.x[1] === blocks.current[track[track.length - 1]].x &&
          solution.y[0] === blocks.current[track[0]].y &&
          solution.y[1] === blocks.current[track[track.length - 1]].y) ||
        (solution.x[1] === blocks.current[track[0]].x &&
          solution.x[0] === blocks.current[track[track.length - 1]].x &&
          solution.y[1] === blocks.current[track[0]].y &&
          solution.y[0] === blocks.current[track[track.length - 1]].y)
      ) {
        props.acceptResponse(solution);
        for (let element of track) {
          blocks.current[element].isCorrect = true;
        }
      }
    }

    preventDefault = -1;
    updateTable();
    track = [];
  };

  const handleGesture = (event: HammerInput) => {
    tracker(event.center.x, event.center.y);
  };

  const revealAnswer = useCallback(
    (solution: ISolution) => {
      props.acceptResponse(solution);

      blocks.current.forEach((block) => {
        const x1 = Number(solution.x[0]);
        const x2 = Number(solution.x[1]);

        const y1 = Number(solution.y[0]);
        const y2 = Number(solution.y[1]);

        if (
          (block.x === solution.x[0] && block.y === solution.y[0]) ||
          (block.x === solution.x[1] && block.y === solution.y[1])
        ) {
          if (x1 !== x2 && y1 === y2) {
            for (let i = x1 - 1; i < x2; i++) {
              blocks.current[i * width + y1 - 1].isCorrect = true;
            }
          } else if (y1 !== y2 && x1 === x2) {
            for (let i = y1 - 1; i < y2; i++) {
              blocks.current[(x1 - 1) * width + i].isCorrect = true;
            }
          } else {
            const biggestX = x1 > x2 ? x1 : x2;
            const minorX = x1 > x2 ? x2 : x1;

            if (y1 > y2) {
              let count = 0;
              for (let i = biggestX; i >= minorX; i--) {
                blocks.current[(i - 1) * width + y2 - 1 + count].isCorrect =
                  true;
                count++;
              }
            } else if (y1 < y2) {
              let count = 0;
              for (let i = biggestX; i >= minorX; i--) {
                blocks.current[(i - 1) * width + y2 - 1 - count].isCorrect =
                  true;
                count++;
              }
            }
          }
        }
      });
    },
    [props, width]
  );

  const checkAndShowLetter = useCallback(
    (
      variable1: number,
      variable2: number,
      constant: number,
      type: string,
      diagonal?: number
    ) => {
      if (variable1 < variable2) {
        if (type === 'x') {
          if (
            blocks.current[(variable1 - 1) * width + constant - 1].isCorrect ===
            true
          ) {
            for (let i = variable1 + 1; i <= variable2; i++) {
              if (i === variable2) {
                props.acceptResponse(props.solutions[0]);
                if (
                  blocks.current[(i - 1) * width + constant - 1].isCorrect !==
                  true
                ) {
                  blocks.current[(i - 1) * width + constant - 1].isCorrect =
                    true;
                  break;
                }
              } else {
                if (
                  blocks.current[(i - 1) * width + constant - 1].isCorrect !==
                  true
                ) {
                  blocks.current[(i - 1) * width + constant - 1].isCorrect =
                    true;
                  break;
                }
              }
            }
          } else {
            blocks.current[(variable1 - 1) * width + constant - 1].isCorrect =
              true;
          }
        } else {
          if (
            blocks.current[(constant - 1) * width + variable1 - 1].isCorrect ===
            true
          ) {
            for (let i = variable1 + 1; i <= variable2; i++) {
              if (i === variable2) {
                props.acceptResponse(props.solutions[0]);
                if (
                  blocks.current[(constant - 1) * width + i - 1].isCorrect !==
                  true
                ) {
                  blocks.current[(constant - 1) * width + i - 1].isCorrect =
                    true;
                }
                break;
              } else {
                if (
                  blocks.current[(constant - 1) * width + i - 1].isCorrect !==
                  true
                ) {
                  blocks.current[(constant - 1) * width + i - 1].isCorrect =
                    true;
                  break;
                }
              }
            }
          } else {
            blocks.current[(constant - 1) * width + variable1 - 1].isCorrect =
              true;
          }
        }
      } else if (variable1 === variable2) {
        if (type === 'x') {
          if (
            blocks.current[(variable1 - 1) * width + constant - 1].isCorrect !==
            true
          ) {
            blocks.current[(variable1 - 1) * width + constant - 1].isCorrect =
              true;
          }
        } else {
          if (
            blocks.current[(constant - 1) * width + variable1 - 1].isCorrect !==
            true
          ) {
            blocks.current[(constant - 1) * width + variable1 - 1].isCorrect =
              true;
          }
        }

        props.acceptResponse(props.solutions[0]);
      }
    },
    [props, width]
  );

  useEffect(() => {
    if (showLetter && !isFinished && props.solutions[0]) {
      const solution = props.solutions[0];

      blocks.current.every((block) => {
        const x1 = Number(solution.x[0]);
        const x2 = Number(solution.x[1]);

        const y1 = Number(solution.y[0]);
        const y2 = Number(solution.y[1]);

        if (
          (block.x === solution.x[0] && block.y === solution.y[0]) ||
          (block.x === solution.x[1] && block.y === solution.y[1])
        ) {
          if (x1 !== x2 && y1 === y2) {
            checkAndShowLetter(x1, x2, y1, 'x');
          } else if (y1 !== y2 && x1 === x2) {
            checkAndShowLetter(y1, y2, x1, 'y');
          } else {
            const biggestX = x1 > x2 ? x1 : x2;

            if (y1 > y2) {
              blocks.current[(biggestX - 1) * width + y2 - 1].isCorrect = true;
            } else if (y1 < y2) {
              blocks.current[(biggestX - 1) * width + y2 - 1].isCorrect = true;
            }
          }
          return false;
        }
        return true;
      });

      updateTable();
      toggleShowLetterHint();
    }
  }, [
    checkAndShowLetter,
    isFinished,
    props.solutions,
    showLetter,
    toggleShowLetterHint,
    width,
  ]);

  useEffect(() => {
    if (showWord && !isFinished) {
      const solution = props.solutions[0];
      revealAnswer(solution);
      updateTable();
      toggleShowWordHint();
    }
  }, [isFinished, props.solutions, revealAnswer, showWord, toggleShowWordHint]);

  useEffect(() => {
    if (isFinished && blocks) {
      props.solutions.forEach((solution) => {
        revealAnswer(solution);
      });
      updateTable();
    }
  }, [isFinished, props.solutions, revealAnswer]);

  useEffect(() => {
    if (resetGame) {
      blocks.current.forEach((block) => {
        block.isCorrect = false;
      });
    }
    updateTable();
    toggleResetGame();
  }, [resetGame, toggleResetGame]);

  return (
    <Gestures
      recognizers={{
        Pan: {
          events: {
            pan: handleGesture,
            panend: validar,
          },
        },
      }}
    >
      <Container ref={(ref) => ref}>{matrix}</Container>
    </Gestures>
  );
};

export default Game;
